import { AnimatePresence } from "framer-motion";
import { Star, UserRound } from "lucide-react";
import { imgSrc } from "../../lib/api";
import { PhotoUploader } from "../PhotoUploader";
import { ActivePill, EditorModal, inputCls } from "./EditorShell";
import { CardActions, EditorHeader, useAdminCollection } from "./useAdminCollection";

const EMPTY = { name: "", role: "", quote: "", stars: 5, img: "", active: true };

export const Avatar = ({ img, name, size = "h-12 w-12", testid }) =>
  img ? (
    <img src={imgSrc(img)} alt={name} className={`${size} shrink-0 rounded-full object-cover ring-2 ring-saffron/40`} data-testid={testid} />
  ) : (
    <span className={`${size} flex shrink-0 items-center justify-center rounded-full bg-ink/5 text-ink/50`} data-testid={testid} aria-hidden="true">
      <UserRound size={20} />
    </span>
  );

export const TestimonialsEditor = () => {
  const { items, editing, setEditing, busy, save, remove, toggleActive } = useAdminCollection("testimonials", "Testimonial");
  const set = (k) => (e) => setEditing({ ...editing, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const { id, order, ...rest } = editing;
    save({ ...rest, stars: Number(editing.stars) });
  };

  return (
    <div data-testid="testimonials-editor">
      <EditorHeader
        summary={`${items.filter((i) => i.active).length} live reviews · shown in the "Loved across the DMV" section on the Home page.${
          items.some((i) => i.source && !i.active) ? ` ${items.filter((i) => i.source && !i.active).length} customer review(s) awaiting your approval — click "Hidden" to publish.` : ""
        }`}
        addLabel="Add Review"
        testid="testimonials-add-btn"
        onAdd={() => setEditing({ ...EMPTY })}
      />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <div key={item.id} data-testid={`testimonials-item-${item.id.slice(0, 8)}`} className={`rounded-2xl border border-inkline bg-parchment p-5 transition-all ${item.active ? "" : "opacity-55"}`}>
            <div className="flex items-center gap-3">
              <Avatar img={item.img} name={item.name} />
              <div className="min-w-0">
                <h4 className="truncate font-display text-base font-semibold text-ink">{item.name}</h4>
                <p className="truncate text-xs text-muted2">{item.role}</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                {item.source && !item.active && (
                  <span className="rounded-full bg-spice/10 px-2 py-0.5 font-mono text-[10px] font-bold text-spice" data-testid={`testimonials-pending-${item.id.slice(0, 8)}`}>Awaiting approval</span>
                )}
                <div className="flex gap-0.5 text-saffron">
                  {Array.from({ length: item.stars }).map((_, s) => <Star key={s} size={12} fill="currentColor" />)}
                </div>
              </div>
            </div>
            <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-ink/80">“{item.quote}”</p>
            <div className="mt-4 flex items-center gap-2">
              <ActivePill active={item.active} onClick={() => toggleActive(item)} testid={`testimonials-active-${item.id.slice(0, 8)}`} />
              <CardActions item={item} prefix="testimonials" onEdit={setEditing} onDelete={remove} />
            </div>
          </div>
        ))}
      </div>
      <AnimatePresence>
        {editing && (
          <EditorModal
            title={editing.id ? "Edit review" : "New customer review"}
            testid="testimonials-form"
            onClose={() => setEditing(null)}
            onSave={submit}
            busy={busy}
            saveLabel={editing.id ? "Save Changes" : "Publish Review"}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <input required data-testid="testimonials-form-name" placeholder="Customer name (e.g. Amina R.)" value={editing.name} onChange={set("name")} className={inputCls} />
              <input data-testid="testimonials-form-role" placeholder="Context (e.g. Wedding, Fairfax VA)" value={editing.role} onChange={set("role")} className={inputCls} />
            </div>
            <textarea required data-testid="testimonials-form-quote" rows={4} placeholder="What did they say?" value={editing.quote} onChange={set("quote")} className={inputCls} />
            <div>
              <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">Rating</div>
              <div className="flex gap-1" data-testid="testimonials-form-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" data-testid={`testimonials-star-${n}`} onClick={() => setEditing({ ...editing, stars: n })}
                    className={`transition-transform hover:scale-110 ${n <= editing.stars ? "text-saffron" : "text-ink/20"}`} aria-label={`${n} star${n > 1 ? "s" : ""}`}>
                    <Star size={24} fill="currentColor" />
                  </button>
                ))}
              </div>
            </div>
            <PhotoUploader value={editing.img} onChange={(img) => setEditing((cur) => ({ ...cur, img }))} folder="testimonials" label="Customer photo (optional)" testid="testimonials-form-img" />
            <label className="flex items-center gap-3 text-sm font-medium text-ink">
              <input type="checkbox" data-testid="testimonials-form-active" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 accent-[#305830]" />
              Live on the Home page
            </label>
          </EditorModal>
        )}
      </AnimatePresence>
    </div>
  );
};
